import React from 'react';

import { AudioManager, createAudioManager } from './audioController';
import { useInstance } from '../../lib/useInstance';

const userInteractionEvents = ['mousedown', 'touchstart'] as const;

const AudioContext = React.createContext<AudioManager>(null as never);

interface AudioProviderProps {
    children: React.ReactNode;
}

export const AudioProvider: React.FC<AudioProviderProps> = (props) => {
    const audioManager = useInstance(createAudioManager);
    const [isInitiallySuspened, setIsInitiallySuspened] = React.useState(false);

    React.useEffect(() => {
        audioManager.setVolume(4);
    }, [audioManager]);

    React.useEffect(() => {
        if (!isInitiallySuspened) {
            return;
        }

        const resume = () => {
            if (!isInitiallySuspened) {
                return;
            }

            setIsInitiallySuspened(false);
        };

        for (const event of userInteractionEvents) {
            window.addEventListener(event, resume, { once: true });
        }

        return () => {
            for (const event of userInteractionEvents) {
                window.removeEventListener(event, resume);
            }
        };
    }, [isInitiallySuspened]);

    return (
        <AudioContext.Provider value={audioManager}>
            {props.children}
        </AudioContext.Provider>
    );
};

export const useAudioContext = () => React.useContext(AudioContext);

export const useVolume = () => {
    const audioAPI = useAudioContext();
    const [volume, setVolume] = React.useState(audioAPI.volumeSignal.value);

    React.useEffect(() => {
        return audioAPI.volumeSignal.listen(setVolume);
    }, [audioAPI.volumeSignal]);

    return volume;
};

export const useMuted = () => {
    const audioAPI = useAudioContext();
    const [muted, setMuted] = React.useState(audioAPI.muteSignal.value);

    React.useEffect(() => {
        return audioAPI.muteSignal.listen(setMuted);
    }, [audioAPI.muteSignal]);

    return muted;
};

export const useMuteSource = () => {
    const [muted, setMuted] = React.useState(false);
    const audioAPI = useAudioContext();
    const muteSource = useInstance(audioAPI.createMuteSource);
    const controller = useInstance(() => ({
        mute() {
            setMuted(true);
            muteSource.mute();
        },
        unmute() {
            setMuted(false);
            muteSource.unmute();
        },
    }));

    return [muted, controller] as const;
};
